import axios from "axios";

import { ENDPOINT } from "constants/environment";
import { encryptData, decryptData, getHeaderConfig } from "helpers";

/**
 * Sign-in for the two b2b2c portals.
 *
 *   POST /app/v1/crm/auth/login      Zenvoya CRM      (ZENVOYA_ROOT | ZENVOYA_B2C)
 *   POST /app/v1/partner/auth/login  Partner portal   (PARTNER)
 *   POST /app/v1/partner/auth/change-password         (both portals)
 *
 * These target `partner_users`, NOT the legacy `users` table behind
 * /auth/adminLogin. They are a different credential store with a different
 * token shape, so the legacy two-step OTP flow in views/auth/SignIn.jsx does
 * not apply and must not be reused here.
 *
 * Transport notes that are easy to get wrong:
 *
 *  - Both login routes are on the server's JWT allow-list, so they need NO
 *    x-access-token. Passing one is harmless; not having one is the point —
 *    they mint the first token.
 *  - Responses are AES-encrypted like every other route, and the payload is
 *    DOUBLE-encoded (the server encrypts an already-serialised JSON string),
 *    so unwrapping is JSON.parse(decryptData(body)) — decryptData parses once,
 *    the caller parses again. This mirrors src/Api/config.js.
 *  - Application failures come back as HTTP **200** carrying
 *    { statusCode: 400, message, data: [] }. Branching on the HTTP status
 *    would read every rejected login as a success, so callers must read
 *    `statusCode` off the decrypted body.
 */

const CRM_LOGIN = `${ENDPOINT}/app/v1/crm/auth/login`;
const PARTNER_LOGIN = `${ENDPOINT}/app/v1/partner/auth/login`;
const CHANGE_PASSWORD = `${ENDPOINT}/app/v1/partner/auth/change-password`;
const SWITCH_BRANCH = `${ENDPOINT}/app/v1/partner/auth/branch`;
const LOGOUT = (portal) => `${ENDPOINT}/app/v1/${portal}/auth/logout`;

/** Shape returned to callers for every outcome, so no call site has to guess. */
const ok = (data) => ({ ok: true, data });
const fail = (message) => ({ ok: false, message });

const GENERIC_FAILURE = "We couldn't sign you in. Please try again.";

/**
 * Unwrap a portal response into { ok, data } | { ok:false, message }.
 *
 * Three different body shapes can arrive and only the first carries the normal
 * envelope:
 *   1. application result  { statusCode, message, data }   (HTTP 200, encrypted)
 *   2. schema rejection    { error: { message, ... } }      (HTTP 400, encrypted)
 *   3. unhandled exception { error: ... }                   (HTTP 500, encrypted)
 */
const unwrap = (raw) => {
  let body;
  try {
    body = JSON.parse(decryptData(raw));
  } catch {
    // Undecryptable — wrong ENCRYPTION_KEY, an HTML error page from a proxy, or
    // an empty body. Nothing useful to show the user beyond the generic text.
    return fail(GENERIC_FAILURE);
  }

  if (body?.statusCode === 200) return ok(body.data);

  // The server's own wording is the only discriminator it gives us — AuthError
  // carries a machine-readable `reason` but never puts it on the wire — so show
  // the message verbatim rather than trying to re-derive a cause from it.
  if (body?.message) return fail(body.message);
  if (body?.error?.message) return fail(body.error.message);
  return fail(GENERIC_FAILURE);
};

/**
 * Login is unauthenticated by design; still send x-api-key via getHeaderConfig.
 *
 * `partnerCode` is sent only by the partner door and is omitted entirely rather
 * than sent empty — the server requires a non-blank value there, and an explicit
 * "" would read as a supplied-but-wrong code.
 */
const postLogin = async (url, email, password, partnerCode) => {
  try {
    const payload = { email, password };
    if (String(partnerCode ?? "").trim()) payload.partnerCode = String(partnerCode).trim();
    const response = await axios.post(
      url,
      encryptData(payload),
      getHeaderConfig(null, ""),
    );
    return unwrap(response?.data);
  } catch (error) {
    // A real transport failure. An application rejection does NOT land here —
    // the server answers those with HTTP 200 — so anything caught is network,
    // CORS, or a 4xx/5xx, and the encrypted body may still hold a message.
    if (error?.response?.data) return unwrap(error.response.data);
    return fail(GENERIC_FAILURE);
  }
};

/** Zenvoya CRM sign-in. Admits ZENVOYA_ROOT and ZENVOYA_B2C users only. */
export const crmLoginAPI = (email, password) => postLogin(CRM_LOGIN, email, password);

/**
 * Partner portal sign-in. Admits PARTNER users only.
 *
 * The partner code is part of the credential: the server refuses it when it is
 * not the code of the company this account belongs to, with the same wording as
 * a wrong password.
 */
export const partnerLoginAPI = (email, password, partnerCode) =>
  postLogin(PARTNER_LOGIN, email, password, partnerCode);

/**
 * MFA step two — redeem the emailed code for a real session.
 *
 * Presents the PRE-AUTH token from step one as `x-access-token`. That token is
 * confined server-side to the verify/resend routes, so it is useless anywhere
 * else; the identity redeeming the code comes from it rather than from the body.
 *
 * The success payload is the SAME shape login returns when MFA is off — session,
 * refresh token, user, partner, role, permissions — so callers can hand it
 * straight to savePortalSession without a second branch.
 */
export const verifyPortalOtpAPI = async (portal, preAuthToken, otp) => {
  try {
    const response = await axios.post(
      `${ENDPOINT}/app/v1/${portal}/auth/verify-otp`,
      encryptData({ otp: String(otp || "").trim() }),
      getHeaderConfig(null, preAuthToken),
    );
    return unwrap(response?.data);
  } catch (error) {
    if (error?.response?.data) return unwrap(error.response.data);
    return fail(GENERIC_FAILURE);
  }
};

/**
 * Ask for a fresh code. Body is empty on purpose — the address is whatever the
 * pre-auth token says it is, so this cannot be pointed at another mailbox.
 * Issuing a new code invalidates the previous one.
 */
export const resendPortalOtpAPI = async (portal, preAuthToken) => {
  try {
    const response = await axios.post(
      `${ENDPOINT}/app/v1/${portal}/auth/resend-otp`,
      encryptData({}),
      getHeaderConfig(null, preAuthToken),
    );
    return unwrap(response?.data);
  } catch (error) {
    if (error?.response?.data) return unwrap(error.response.data);
    return fail(GENERIC_FAILURE);
  }
};

/**
 * First-login password rotation. Serves BOTH portals (there is no /crm variant).
 *
 * Unlike the login routes this one REQUIRES a valid x-access-token — the acting
 * user is taken from the token, never from the body, so a caller cannot rotate
 * someone else's password. The server returns no new token, so the existing one
 * stays valid and only the cached mustChangePassword flag needs clearing.
 */
export const changePasswordAPI = async (accessJWT, currentPassword, newPassword) => {
  try {
    const response = await axios.post(
      CHANGE_PASSWORD,
      encryptData({ currentPassword, newPassword }),
      getHeaderConfig(null, accessJWT),
    );
    return unwrap(response?.data);
  } catch (error) {
    if (error?.response?.data) return unwrap(error.response.data);
    return fail(GENERIC_FAILURE);
  }
};

/**
 * Work at a different branch of your own agency — partner console only.
 *
 * Answers a NEW access token whose `branch_code` claim is the chosen branch; the
 * caller swaps it in and every later request is scoped by it. The branch is the
 * ONLY thing this can change — the partner comes from the verified token, and
 * the server refuses a branch that is not that partner's.
 *
 * There is no server-side record of the choice, deliberately: `/refresh`
 * re-reads the user row and answers their own branch again, so the portal
 * re-applies this after a refresh. See `actingBranchCode` in
 * helpers/portalSession.js.
 */
export const switchBranchAPI = async (accessJWT, branchCode) => {
  try {
    const response = await axios.post(
      SWITCH_BRANCH,
      encryptData({ branchCode: Number(branchCode) }),
      getHeaderConfig(null, accessJWT),
    );
    return unwrap(response?.data);
  } catch (error) {
    if (error?.response?.data) return unwrap(error.response.data);
    return fail("We couldn't change the branch. Please try again.");
  }
};

/**
 * Revoke the session server-side (S-204).
 *
 * Registered for both portals, so `portal` picks the door. Always answers success
 * — a caller signing out should not learn whether the token it presented was
 * real — so treat a failure here as a transport problem, not a refusal, and clear
 * local state either way.
 */
export const logoutAPI = async (portal, refreshToken) => {
  try {
    const response = await axios.post(
      LOGOUT(portal),
      encryptData({ refreshToken }),
      getHeaderConfig(null, ""),
    );
    return unwrap(response?.data);
  } catch (error) {
    if (error?.response?.data) return unwrap(error.response.data);
    return fail(GENERIC_FAILURE);
  }
};
